import React, { useEffect, useState } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { devportalApi } from '../../../utils/devportalApi';

const UsageAnalytics = ({ projectId }) => {
  const [timeRange, setTimeRange] = useState('7d');
  const [activeChart, setActiveChart] = useState('requests');
  const [series, setSeries] = useState([]);
  const [summary, setSummary] = useState({ total_requests: 0, error_rate: 0, avg_response_time_ms: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const timeRanges = [
    { value: '24h', label: '24 Hours' },
    { value: '7d', label: '7 Days' },
    { value: '30d', label: '30 Days' },
    { value: '90d', label: '90 Days' }
  ];

  const chartTypes = [
    { value: 'requests', label: 'API Requests', icon: 'BarChart3' },
    { value: 'messages', label: 'Message Sent', icon: 'BarChart3' },
    { value: 'errors', label: 'Error Rate', icon: 'AlertTriangle' },
    { value: 'response', label: 'Response Time', icon: 'Clock' }
  ];

  const getChartData = () => {
    switch (activeChart) {
      case 'errors':
        return series?.map(d => ({ ...d, value: d?.errors }));
      case 'response':
        return series?.map(d => ({ ...d, value: d?.avg_response_time }));
      case 'messages':
        return series?.map(d => ({ ...d, value: d?.messages }));
      default:
        return series?.map(d => ({ ...d, value: d?.requests }));
    }
  };

  const getChartColor = () => {
    switch (activeChart) {
      case 'errors':
        return '#DC2626';
      case 'response':
        return '#D97706';
      default:
        return '#3B82F6';
    }
  };

  const formatValue = (value) => {
    switch (activeChart) {
      case 'errors':
        return `${value} errors`;
      case 'response':
        return `${value}ms`;
      default:
        return value?.toLocaleString();
    }
  };

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const data = await devportalApi.getUsageAnalytics({ range: timeRange, project_id: projectId });
        const normalized = Array.isArray(data?.items) ? data.items : (Array.isArray(data) ? data : []);
        if (mounted) setSeries(normalized);
      } catch (e) {
        if (mounted) setError(e?.message || 'Failed to load analytics');
      } finally {
        setLoading(false);
      }
      try {
        const sum = await devportalApi.getAnalyticsSummary();
        if (mounted) setSummary({
          total_requests: sum?.total_requests ?? 0,
          error_rate: sum?.error_rate ?? 0,
          avg_response_ms: sum?.avg_response_time_ms ?? 0
        });
      } catch (_) {}
    };
    load();
    return () => { mounted = false; };
  }, [timeRange]);

  return (
    <div className="bg-card rounded-lg border border-border p-6">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between mb-6 space-y-4 lg:space-y-0">
        <h2 className="text-xl font-semibold text-foreground">Usage Analytics</h2>
        
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center space-x-2">
            {chartTypes?.map((type) => (
              <Button
                key={type?.value}
                variant={activeChart === type?.value ? "default" : "ghost"}
                size="sm"
                iconName={type?.icon}
                iconPosition="left"
                onClick={() => setActiveChart(type?.value)}
                className="text-xs"
              >
                {type?.label}
              </Button>
            ))}
          </div>
          
          <div className="flex items-center space-x-2">
            {timeRanges?.map((range) => (
              <Button
                key={range?.value}
                variant={timeRange === range?.value ? "default" : "outline"}
                size="sm"
                onClick={() => setTimeRange(range?.value)}
                className="text-xs"
              >
                {range?.label}
              </Button>
            ))}
          </div>
        </div>
      </div>
      {error && (
        <div className="mb-4 flex items-center space-x-2 text-destructive bg-destructive/10 p-3 rounded-md">
          <Icon name="AlertCircle" size={16} />
          <span className="text-sm">{error}</span>
        </div>
      )}
      
      {!loading && !error && (!series || series.length === 0) && (
        <div className="h-80 w-full mb-6 flex flex-col items-center justify-center text-center">
          <div className="mb-4">
            <Icon name="BarChart3" size={48} className="text-muted-foreground/50 mx-auto" />
          </div>
          <h3 className="text-lg font-medium text-foreground mb-2">No Data Available</h3>
          <p className="text-sm text-muted-foreground max-w-md">
            There's no usage data available for the selected time period. This could mean:
          </p>
          <ul className="text-sm text-muted-foreground mt-2 space-y-1">
            <li>• The project hasn't received any API requests yet</li>
            <li>• No data has been collected for this time range</li>
            <li>• Analytics tracking hasn't been enabled for this project</li>
          </ul>
          <div className="mt-4">
            <Button
              variant="outline"
              size="sm"
              iconName="RefreshCw"
              iconPosition="left"
              onClick={() => window.location.reload()}
            >
              Refresh Data
            </Button>
          </div>
        </div>
      )}
      
      {(!loading && !error && series && series.length > 0) && (
        <div className="h-80 w-full mb-6">
        <ResponsiveContainer width="100%" height="100%">
          {activeChart === 'requests' ? (
            <BarChart data={getChartData()}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(148, 163, 184, 0.2)" />
              <XAxis 
                dataKey="date" 
                stroke="#94A3B8"
                fontSize={12}
              />
              <YAxis 
                stroke="#94A3B8"
                fontSize={12}
                tickFormatter={(value) => value?.toLocaleString()}
              />
              <Tooltip 
                contentStyle={{
                  backgroundColor: '#1E293B',
                  border: '1px solid rgba(148, 163, 184, 0.2)',
                  borderRadius: '8px',
                  color: '#F8FAFC'
                }}
                formatter={(value) => [formatValue(value), 'Requests']}
              />
              <Bar dataKey="value" fill={getChartColor()} radius={[4, 4, 0, 0]} />
            </BarChart>
          ) : (
            <LineChart data={getChartData()}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(148, 163, 184, 0.2)" />
              <XAxis 
                dataKey="date" 
                stroke="#94A3B8"
                fontSize={12}
              />
              <YAxis 
                stroke="#94A3B8"
                fontSize={12}
                tickFormatter={(value) => activeChart === 'response' ? `${value}ms` : value}
              />
              <Tooltip 
                contentStyle={{
                  backgroundColor: '#1E293B',
                  border: '1px solid rgba(148, 163, 184, 0.2)',
                  borderRadius: '8px',
                  color: '#F8FAFC'
                }}
                formatter={(value) => [formatValue(value), chartTypes?.find(t => t?.value === activeChart)?.label]}
              />
              <Line 
                type="monotone" 
                dataKey="value" 
                stroke={getChartColor()} 
                strokeWidth={3}
                dot={{ fill: getChartColor(), strokeWidth: 2, r: 4 }}
                activeDot={{ r: 6, stroke: getChartColor(), strokeWidth: 2 }}
              />
            </LineChart>
          )}
        </ResponsiveContainer>
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="p-4 bg-muted/30 rounded-lg border border-border">
          <div className="flex items-center space-x-2 mb-2">
            <Icon name="TrendingUp" size={16} className="text-accent" />
            <span className="text-sm text-muted-foreground">Total Requests</span>
          </div>
          <p className="text-2xl font-bold text-foreground">{summary?.total_requests?.toLocaleString?.() || 0}</p>
          <p className="text-xs text-accent">&nbsp;</p>
        </div>

        <div className="p-4 bg-muted/30 rounded-lg border border-border">
          <div className="flex items-center space-x-2 mb-2">
            <Icon name="AlertTriangle" size={16} className="text-warning" />
            <span className="text-sm text-muted-foreground">Error Rate</span>
          </div>
          <p className="text-2xl font-bold text-foreground">{(summary?.error_rate ?? 0)}%</p>
          <p className="text-xs text-accent">&nbsp;</p>
        </div>

        <div className="p-4 b-muted/30 rounded-lg border border-border">
          <div className="flex items-center space-x-2 mb-2">
            <Icon name="Clock" size={16} className="text-secondary" />
            <span className="text-sm text-muted-foreground">Avg Response</span>
          </div>
          <p className="text-2xl font-bold text-foreground">{summary?.avg_response_ms ?? 0}ms</p>
          <p className="text-xs text-accent">&nbsp;</p>
        </div>
      </div>
    </div>
  );
};

export default UsageAnalytics;